/**
 * API error extraction utilities.
 *
 * Extracts human-readable messages from backend error responses.
 * The backend returns errors in several formats — all with a `detail` field
 * that can be a string, dict, or array. These utilities normalize that into
 * a single message string.
 *
 * IMPORTANT: the axios layer does not camelCase-convert error responses
 * (only success responses), so raw `error.response.data` keys arrive in
 * snake_case. `extractApiErrorData` applies convertKeysToCamel itself (F5)
 * so its callers see the same camelCase shape as success data.
 */
import { type AxiosError } from 'axios';
import { convertKeysToCamel } from './dataTransform';
import { SERVER_FAILURE_MESSAGE } from '@/constants/alertMessages';
import { isAccountMergedRetry } from '@/api/failureClass';

// F4: cap user-visible error messages at 500 chars + ellipsis.
const MAX_MESSAGE_LEN = 500;
function truncate(s: string): string {
  return s.length > MAX_MESSAGE_LEN ? s.slice(0, MAX_MESSAGE_LEN) + '…' : s;
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Dict-style detail (consume access control errors). */
export interface ApiErrorDetail {
  error?: string;
  message?: string;
  [key: string]: unknown;
}

/** Pydantic validation error item. */
export interface PydanticValidationError {
  loc: (string | number)[];
  msg: string;
  type: string;
}

/**
 * Structured API error response data, as returned by extractApiErrorData.
 *
 * Fields are camelCase: extractApiErrorData runs the raw snake_case error
 * body through convertKeysToCamel (F5, symmetric with success responses).
 */
export interface ApiErrorData {
  detail?: string | ApiErrorDetail | PydanticValidationError[];
  field?: string;
  entityType?: string;
  entityId?: number | string;
  invitationId?: number;
  violations?: Array<{ type: string; params?: string[] }>;
  [key: string]: unknown;
}

// ---------------------------------------------------------------------------
// Type guards
// ---------------------------------------------------------------------------

/** Check if an error is an AxiosError with a response attached. */
export function isAxiosErrorWithResponse(
  error: unknown,
): error is AxiosError<ApiErrorData | string> & { response: NonNullable<AxiosError['response']> } {
  return (
    error != null &&
    typeof error === 'object' &&
    'isAxiosError' in error &&
    (error as AxiosError).isAxiosError === true &&
    (error as AxiosError).response != null
  );
}

// ---------------------------------------------------------------------------
// Extractors
// ---------------------------------------------------------------------------

/**
 * Extract a human-readable message from any error shape.
 *
 * Priority:
 *   1. AxiosError → response.data.detail (string)
 *   2. AxiosError → response.data.detail.message (dict pattern)
 *   3. AxiosError → response.data.detail[0].msg (Pydantic array)
 *   4. AxiosError → response.data as plain string (non-JSON body)
 *   5. Non-Axios Error.message (only if not Axios generic)
 *   6. fallback
 *
 * A 5xx (#1185, owner Q-296 a) shows the server's own sentence: a string
 * `detail` (1) or the dict pattern's message (2). Anything else (an HTML or
 * text body from a proxy, an empty body, JSON without a usable `detail`) is
 * SERVER_FAILURE_MESSAGE, the sentence vingapi sends, and the caller's
 * `fallback` is ignored: that copy is for 4xx and no-status failures. The
 * body's `type` (the server's exception class) is never read.
 *
 * A 409 `account_merged_retry` (#1336) is a server error to every surface (the
 * client already resent it once where the server proved that safe): it gets the
 * central server copy and its own `detail.message` is NEVER returned, on any
 * route. This is the one place every message-printing caller reads the body.
 */
export function extractApiMessage(error: unknown, fallback: string): string {
  if (isAccountMergedRetry(error)) return SERVER_FAILURE_MESSAGE;
  if (isAxiosErrorWithResponse(error)) {
    const data = error.response.data;
    const serverFailed = error.response.status >= 500;

    // F4: previously dropped strings >= 200 chars to fallback; now truncate.
    // A 5xx string body is a proxy's HTML/text, never something to show (#1185).
    if (typeof data === 'string' && data.length > 0 && !serverFailed) {
      return truncate(data);
    }

    if (data && typeof data === 'object') {
      const { detail } = data;

      if (typeof detail === 'string' && detail.length > 0) {
        return truncate(detail);
      }

      if (detail && typeof detail === 'object' && !Array.isArray(detail)) {
        const detailObj = detail as ApiErrorDetail;
        if (typeof detailObj.message === 'string' && detailObj.message.length > 0) {
          return truncate(detailObj.message);
        }
      }

      if (Array.isArray(detail) && detail.length > 0 && !serverFailed) {
        const first = detail[0];
        if (first && typeof first === 'object' && 'msg' in first) {
          const loc = (first as PydanticValidationError).loc
            ?.filter((segment) => segment !== 'body')
            ?.join(' > ');
          const msg = (first as PydanticValidationError).msg;
          return truncate(loc ? `${loc}: ${msg}` : msg);
        }
      }
    }

    if (serverFailed) return SERVER_FAILURE_MESSAGE;
  }

  if (error instanceof Error) {
    const msg = error.message;
    if (msg && !msg.startsWith('Request failed with status code')) {
      return truncate(msg);
    }
  }

  return fallback;
}

/**
 * Extract the full structured error data from an API error, if available.
 * Useful for programmatic handling (e.g., checking `field` for form errors).
 */
export function extractApiErrorData(error: unknown): ApiErrorData | null {
  if (isAxiosErrorWithResponse(error)) {
    const data = error.response.data;
    if (data && typeof data === 'object') {
      // F5: run error bodies through convertKeysToCamel for symmetry with success.
      return convertKeysToCamel<ApiErrorData>(data);
    }
  }
  return null;
}

/**
 * Extract the machine-readable error code from dict-style detail responses.
 * Returns the `error` field (e.g., "authentication_required") or null.
 */
export function extractApiErrorCode(error: unknown): string | null {
  const data = extractApiErrorData(error);
  if (data) {
    const { detail } = data;
    if (detail && typeof detail === 'object' && !Array.isArray(detail)) {
      const code = (detail as ApiErrorDetail).error;
      return typeof code === 'string' ? code : null;
    }
  }
  return null;
}
